import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FiTrash2 } from "react-icons/fi";
import { FaClock } from "react-icons/fa";
import { GrDocumentPdf } from "react-icons/gr";
import { makeDeleteRequest, makeFetchRequest } from "../utilities/common";
import { defaultUrl } from "../features/actions";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";

const PRIMARY = "#0F2A6B";

export const StudentActivityFeed = () => {
  const [activities, setActivities] = useState<any[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { pupil_id } = useParams();

  useEffect(() => {
    fetchActivities();
  }, []);

  /* ================= FETCH ================= */

  async function fetchActivities() {
    try {
      toast.loading("Loading activities...");
      const res = await makeFetchRequest(
        `${defaultUrl}/activity_feed?student_id=${pupil_id}&per_page=100`,
      );

      const cleaned = res.map((item: any) => ({
        ...item,
        image: item.image && item.image !== "null" ? item.image : null,
        video: item.video && item.video !== "null" ? item.video : null,
        formattedTime: formatDate(item.activity_time),
      }));

      setActivities(cleaned);
      toast.dismiss();
    } catch {
      toast.dismiss();
      toast.error("Failed to fetch");
    }
  }

  /* ================= DATE FORMAT ================= */

  function formatDate(dateString: string) {
    const date = new Date(dateString);

    const now = new Date();
    const diff = (now.getTime() - date.getTime()) / 1000;

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /* ================= DELETE ================= */

  async function deleteActivity(item: any) {
    Swal.fire({
      title: "Delete Activity?",
      text: "This cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "red",
    }).then((res) => {
      if (res.isConfirmed) {
        makeDeleteRequest(`${defaultUrl}/activity_feed/${item.id}`).then(
          fetchActivities,
        );
      }
    });
  }

  /* ================= UI ================= */

  return (
    <div className="bg-blue-50 min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        {/* HEADER */}
        <div className="flex items-center mb-6">
          <BackButton />
          <TopAdminNav name="Pupil" title="Activity Timeline" />
        </div>

        {/* TIMELINE */}
        <div className="relative border-l-2 border-blue-100 ml-4 space-y-8">
          {activities.length ? (
            activities.map((item, i) => (
              <div key={i} className="relative">
                {/* DOT */}
                <div
                  className="absolute -left-[10px] top-3 w-4 h-4 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: PRIMARY }}
                />

                {/* CARD */}
                <div className="ml-6 bg-white rounded-xl p-4 border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
                  {/* HEADER */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-gray-800 capitalize">
                        {item.activity_type}
                      </p>

                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <FaClock /> {item.formattedTime}
                      </p>
                    </div>

                    <button
                      onClick={() => deleteActivity(item)}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition"
                    >
                      <FiTrash2 />
                    </button>
                  </div>

                  {/* DATA GRID */}
                  {item.data && (
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      {Object.entries(item.data).map(([k, v]: any, idx) => (
                        <div
                          key={idx}
                          className="bg-gray-50 rounded-md px-2 py-1"
                        >
                          <span className="text-gray-400 text-xs capitalize">
                            {k}
                          </span>
                          <p className="text-gray-700">
                            {Array.isArray(v) ? v.join(", ") : v}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* DESCRIPTION */}
                  {item.description && (
                    <p className="text-sm text-gray-600 mb-3">
                      {item.description}
                    </p>
                  )}

                  {/* IMAGE */}
                  {item.image && (
                    <img
                      src={item.image}
                      onClick={() => setPreviewImage(item.image)}
                      className="rounded-lg max-h-60 object-cover cursor-pointer hover:opacity-90 transition mb-3"
                    />
                  )}

                  {/* VIDEO */}
                  {item.video && (
                    <video
                      controls
                      className="w-full rounded-lg max-h-[300px] object-cover bg-black mb-3"
                    >
                      <source src={item.video} type="video/mp4" />
                    </video>
                  )}

                  {/* PDF */}
                  {item.doc && (
                    <a
                      href={item.doc}
                      className="flex items-center gap-2 text-blue-600 hover:underline"
                    >
                      <GrDocumentPdf />
                      Download PDF
                    </a>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-400 py-20">
              No activity yet
            </div>
          )}
        </div>
      </div>

      {/* IMAGE PREVIEW MODAL */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
        >
          <img
            src={previewImage}
            className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};
